import { Injectable } from '@angular/core';
import { EditorTree } from './editor-tree-service/editor-tree';
import { EditorTreeNode } from './editor-tree-service/editor-tree-node';
import { ActivityHierarchyResponse } from '../api/data-definitions/models/api/activity-hierarchy-response';
import { ActivityGroupResponse } from '../api/data-definitions/models/api/activity-group-response';
import { AccountingScope, Tree, TreeAccounting, TreeCategory } from './backend/model/tree';

@Injectable({ providedIn: 'root' })
export class ActivityHierarchyTreeService {
  buildNavigationTree(
    activityHierarchy: ActivityHierarchyResponse,
    boardId: string,
    boardTitle: string,
    cutoffDepth?: number,
    includeExcludeEmissionGroups: boolean = false
  ): EditorTree {
    const id = boardId;
    activityHierarchy.groups = activityHierarchy.groups ?? [];
    const children = activityHierarchy.groups
      .map((group: any) => this._buildNavigationTreeChild(group, id, 1, cutoffDepth, includeExcludeEmissionGroups))
      .filter((node) => node !== null);
    return {
      id,
      title: boardTitle,
      children,
      expandable: children.length > 0,
    };
  }

  buildTree(activityHierarchy: ActivityHierarchyResponse, boardId: string, boardTitle: string): Tree {
    const id = boardId;
    activityHierarchy.groups = activityHierarchy.groups ?? [];
    return {
      type: 'DISPLAY',
      name: boardTitle,
      title: activityHierarchy.title,
      version: activityHierarchy.version,
      friendlyName: activityHierarchy.name,
      categories: activityHierarchy.groups.map((child: any) => this._buildTreeCategory(child, id, 1)),
    };
  }

  private _buildTreeCategory(group: ActivityGroupResponse, parentId: string, depth: number): TreeCategory {
    group.sub_activity_groups = group.sub_activity_groups ?? [];
    const activities: TreeAccounting[] =
      group.activities?.map((activity) => ({
        id: activity.id,
        ccc: activity.id,
        scope: this._matchScope(activity.scope),
      })) ?? [];
    return {
      name: group.title,
      friendlyName: group.title,
      subCategories: group.sub_activity_groups
        .map((child: any) => this._buildTreeCategory(child, group.id, depth + 1))
        .filter(Boolean),
      id: group.id,
      excludeEmissions: group.excludeEmissions,
      source: '',
      version: '',
      refNo: '',
      order: group.order,
      tree: parentId,
      accounting: activities,
      variables: [],
    };
  }

  private _buildNavigationTreeChild(
    group: ActivityGroupResponse,
    parentId: string,
    depth: number,
    cutoffDepth?: number,
    includeExcludeEmissionGroups: boolean = false
  ): EditorTreeNode | null {
    group.sub_activity_groups = group.sub_activity_groups ?? [];
    if (!includeExcludeEmissionGroups && group.excludeEmissions) {
      return null;
    }
    let proceedProcessing = true;
    if (cutoffDepth && depth === cutoffDepth) {
      proceedProcessing = false;
    }

    const children = proceedProcessing
      ? (group.sub_activity_groups
          ?.map((child: any) => this._buildNavigationTreeChild(child, group.id, depth + 1, cutoffDepth))
          .filter((node) => node !== null) ?? [])
      : [];

    return {
      id: group.id,
      title: group.title,
      expandable: children.length > 0,
      depth,
      parentId,
      children,
    };
  }

  private _matchScope(scope: number | undefined): AccountingScope | undefined {
    if (scope === undefined) {
      return undefined;
    }

    switch (scope) {
      case 1: return AccountingScope.SCOPE_1;
      case 2: return AccountingScope.SCOPE_2;
      case 3: return AccountingScope.SCOPE_3;
      default: return undefined;
    }
  }
}
